/**
 * ThemeColorSync Component
 * Renderless component that syncs CraftJS node color props when theme changes.
 * Replaces old theme hex colors with new theme colors across all nodes.
 */
import { useEffect, useRef } from "react";
import { useEditor } from "@craftjs/core";
import { ThemeSettings } from "../../types";

interface ThemeColorSyncProps {
  theme: ThemeSettings;
}

/**
 * Build a case-insensitive hex color replacement map from old → new theme.
 * Only includes entries where the color actually changed.
 */
const buildColorMap = (
  oldTheme: ThemeSettings,
  newTheme: ThemeSettings
): Map<string, string> => {
  const map = new Map<string, string>();
  if (oldTheme.primary.toLowerCase() !== newTheme.primary.toLowerCase()) {
    map.set(oldTheme.primary.toLowerCase(), newTheme.primary);
  }
  if (oldTheme.secondary.toLowerCase() !== newTheme.secondary.toLowerCase()) {
    map.set(oldTheme.secondary.toLowerCase(), newTheme.secondary);
  }
  return map;
};

/**
 * Replace all occurrences of old colors in a gradient/color string.
 */
const replaceColorsInString = (
  value: string,
  colorMap: Map<string, string>
): string => {
  let result = value;
  colorMap.forEach((newColor, oldColor) => {
    // Case-insensitive global replace of hex color
    const regex = new RegExp(oldColor.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    result = result.replace(regex, newColor);
  });
  return result;
};

/**
 * Definitions of which props to sync per component type.
 * - `colorProps`: direct hex color props (exact match & replace)
 * - `stringProps`: props containing color in a larger string (e.g. gradient), use regex replace
 * - `slideColorProps`: props inside slides[] array items
 */
const COMPONENT_COLOR_CONFIG: Record<
  string,
  {
    colorProps?: string[];
    stringProps?: string[];
    slideColorProps?: string[];
  }
> = {
  Hero: {
    colorProps: ["backgroundColor"],
  },
  PromotionBanner: {
    colorProps: ["backgroundColor", "accentColor"],
  },
  FAQ: {
    colorProps: ["accentColor"],
  },
  HeroSlider: {
    slideColorProps: ["backgroundColor"],
  },
  Container: {
    stringProps: ["background"],
  },
};

export const ThemeColorSync: React.FC<ThemeColorSyncProps> = ({ theme }) => {
  const prevThemeRef = useRef<ThemeSettings>(theme);
  const { actions, query } = useEditor();

  useEffect(() => {
    const oldTheme = prevThemeRef.current;
    prevThemeRef.current = theme;

    // Build color replacement map
    const colorMap = buildColorMap(oldTheme, theme);
    if (colorMap.size === 0) return; // No color changes

    // Iterate all CraftJS nodes
    const nodes = query.getSerializedNodes();

    Object.entries(nodes).forEach(([nodeId, node]) => {
      const componentName = (node.type as any)?.resolvedName;
      if (!componentName) return;

      const config = COMPONENT_COLOR_CONFIG[componentName];
      if (!config) return;

      const props = node.props || {};
      let hasChanges = false;

      // Direct color props (exact hex match)
      if (config.colorProps) {
        for (const propName of config.colorProps) {
          const value = props[propName];
          if (typeof value === "string") {
            const mapped = colorMap.get(value.toLowerCase());
            if (mapped) {
              hasChanges = true;
              actions.setProp(nodeId, (p: any) => {
                p[propName] = mapped;
              });
            }
          }
        }
      }

      // String props (gradient strings containing hex colors)
      if (config.stringProps) {
        for (const propName of config.stringProps) {
          const value = props[propName];
          if (typeof value === "string") {
            const replaced = replaceColorsInString(value, colorMap);
            if (replaced !== value) {
              hasChanges = true;
              actions.setProp(nodeId, (p: any) => {
                p[propName] = replaced;
              });
            }
          }
        }
      }

      // Slide array props (e.g. HeroSlider slides[].backgroundColor)
      if (config.slideColorProps && Array.isArray(props.slides)) {
        const newSlides = props.slides.map((slide: any) => {
          let slideChanged = false;
          const updatedSlide = { ...slide };

          for (const propName of config.slideColorProps!) {
            const value = slide[propName];
            if (typeof value === "string") {
              const mapped = colorMap.get(value.toLowerCase());
              if (mapped) {
                updatedSlide[propName] = mapped;
                slideChanged = true;
              }
            }
          }

          return slideChanged ? updatedSlide : slide;
        });

        // Only update if at least one slide changed
        if (newSlides.some((s: any, i: number) => s !== props.slides[i])) {
          actions.setProp(nodeId, (p: any) => {
            p.slides = newSlides;
          });
        }
      }
    });
  }, [theme, actions, query]);

  return null;
};

export default ThemeColorSync;
